import * as R from 'ramda'
import React, { useEffect, useRef, useState } from 'react'
import auth from 'auth'
import { Container, Button, Segment, Form, Dropdown } from 'semantic-ui-react'
import { Link, useHistory } from 'react-router-dom'
import store from 'store'
import { resetStore } from 'libs/connectApi'
import { getStore } from '../../store'

import Table from 'shared/components/Table'
import { AdminContainer } from 'pages/Admin'
import ContainerShell from 'shared/components/ContainerShell'
import ContainerShellIntro from 'shared/components/ContainerShellIntro'
import constants from 'config/constants'
import useAxios, { cache, mutate } from 'hooks/useAxios'
import PageHeaderContainer from 'shared/components/PageHeader/container'
import useModal from 'hooks/useModal'
import toast from 'libs/toast'
import { Message } from 'semantic-ui-react'
import Popup from 'shared/components/Popup'
import { fullDateTimeFormat, relativeDateTimeFormat } from 'libs/date-format'
import useSaveUserActivityLog, { ACTION_NAME } from 'hooks/useSaveUserActivityLog'
import generateUserActivityLog from 'libs/generateUserActivityLog'
import { Redirect } from 'react-router-dom'
import ErrorText from 'shared/components/forms/ErrorText'

const WORKSPACE_NAME_REQUIRED_ERROR = 'Workspace name is required.'
const GENERIC_SAVE_ERROR = 'Could not save changes. Contact support for help.'

export default function SelectWorkspace() {
  const {
    Modal: CreateModal,
    open: openCreateModal,
    isOpen: isOpenCreateModal,
    close: closeCreateModal,
  } = useModal()

  const {
    Modal: DeleteModal,
    open: openDeleteModal,
    isOpen: isOpenDeleteModal,
    close: closeDeleteModal,
  } = useModal()

  const [loading, setLoading] = useState(true)
  const [workspaceName, setWorkspaceName] = useState('')
  const [workspaceNameError, setWorkspaceNameError] = useState('')
  const [workspaceToken, setWorkspacetoken] = useState('')
  const hiddenRefButton = useRef(null)

  const userInfo = auth.getUserInfoFromToken()

  const { data } = useAxios({
    url: 'account/workspaces/',
  })
  const { fetch: createWorkspace, loading: loadingCreateWorkspace } = useAxios({
    method: 'post',
    url: 'account/workspaces/',
    skip: true,
  })

  const { fetch: deleteWorkspace, loading: loadingDeleteWorkspace } = useAxios({
    method: 'DELETE',
    url: 'account/leave-workspace/',
    skip: true,
  })

  const history = useHistory()
  const saveUserActivityLog = useSaveUserActivityLog()
  const loggedInMethod = R.path(['state', 'loggedInMethod'], history.location)
  const previousUrl = R.path(['state', 'previousUrl'], history.location)

  function handelLogout() {
    auth.logout()
    history.push('/login')
  }

  function handleCreateWorkspace() {
    const trimmedName = workspaceName.trim()
    if (!trimmedName) {
      setWorkspaceNameError(WORKSPACE_NAME_REQUIRED_ERROR)
      return
    }

    setWorkspaceNameError('')
    setWorkspaceName(trimmedName)

    const data = {
      name: trimmedName,
    }

    createWorkspace({ data })
      .then((res) => {
        if (res.data) {
          mutate('account/workspaces/', false)
          toast.success('New workspace created!')
          handleCloseWorkspaceModal()
        }
      })
      .catch((err) => {
        toast.error(GENERIC_SAVE_ERROR)
        console.error(err)
      })
  }

  function handleLeaveWorkspace(workspaceToken) {
    setWorkspacetoken(workspaceToken)
    openDeleteModal()
  }

  function handleDeleteWorkspace() {
    const request = generateUserActivityLog({
      action: 'leave_workspace',
      target: workspaceToken,
      statusCode: 200,
    })

    const data = {
      browser: request.browser,
      locale: request.locale,
      timezone: request.timezone,
    }

    deleteWorkspace({
      headers: {
        'X-Workspace-Token': workspaceToken,
      },
      data,
    })
      .then((res) => {
        if (res.status == 204) {
          mutate(
            'account/workspaces/',
            (state) => {
              return {
                ...state,
                results: state.results.filter(
                  (workspace) => workspace.workspace_token !== workspaceToken
                ),
              }
            },
            false
          )

          toast.success("You've been removed from the workspace.")
          closeDeleteModal()
        }
      })
      .catch((err) => {
        toast.error(
          'Uh oh. There was a problem leaving the workspace. Please try again or contact Simplesat support.'
        )
        console.error(err)
      })
  }

  function saveSignedInActivityLog(companyId, workspaceToken) {
    const userInfo = auth.getUserInfoFromToken()

    saveUserActivityLog({
      action: ACTION_NAME.signed_in,
      target: `com_${companyId}`,
      statusCode: 200,
      requestBody: { ...userInfo, logged_in_method: loggedInMethod },
      headers: { 'X-Workspace-Token': workspaceToken },
    })
  }

  function handleCloseWorkspaceModal() {
    setWorkspaceName('')
    setWorkspaceNameError('')
    closeCreateModal()
  }

  function renderTimestampField(timestamp) {
    if (timestamp === null || timestamp === undefined) {
      return '-'
    }
    return (
      <Popup basic tooltip={fullDateTimeFormat(timestamp)} size="mini" inverted>
        <p>{relativeDateTimeFormat(timestamp)}</p>
      </Popup>
    )
  }

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      width: '50%',
      render(_, workspace) {
        return (
          <Link
            to={
              previousUrl
                ? `/${workspace.workspace_token}${previousUrl}`
                : `/${workspace.workspace_token}`
            }
            onClick={() => {
              saveSignedInActivityLog(workspace.id, workspace.workspace_token)
              store.set(constants.simplesatWorkspaceTokenName, workspace.workspace_token)
            }}
          >
            {workspace.name}
          </Link>
        )
      },
    },
    {
      title: 'Workspace ID',
      dataIndex: 'workspace_token',
      key: 'workspace_token',
      ellipsis: true,
      width: '20%',
    },
    {
      title: 'Last accessed',
      dataIndex: 'last_logged_in',
      key: 'last_logged_in',
      ellipsis: true,
      width: '20%',
      render: renderTimestampField,
    },

    {
      title: '',
      key: 'action',
      align: 'right',
      width: '10%',
      render(_, data) {
        if (data.workspace_owner_user_id != userInfo.user_id) {
          return (
            <Dropdown
              icon="far fa-ellipsis-vertical"
              floating
              pointing="top right"
              className="table-elipsis-toolbar-dropdown"
            >
              <Dropdown.Menu>
                <React.Fragment>
                  <React.Fragment>
                    <Dropdown.Item
                      className="!text-negative"
                      text="Leave workspace"
                      icon="far fa-sign-out-alt"
                      onClick={() => handleLeaveWorkspace(data.workspace_token)}
                    />
                  </React.Fragment>
                </React.Fragment>
              </Dropdown.Menu>
            </Dropdown>
          )
        } else {
          return null
        }
      },
    },
  ]

  useEffect(() => {
    // Clear APIs cache, so when swtich between workspaces,
    // it will not briefly  show previous api data
    getStore().dispatch(resetStore())
    cache.clear()
  }, [])

  useEffect(() => {
    if (data) {
      setLoading(false)
    }
  }, [data])

  if (auth.isDemoUser()) {
    return <Redirect to="/" />
  }

  return (
    <React.Fragment>
      <Container className="!h-full !w-full">
        <PageHeaderContainer
          title="Simplesat workspaces"
          rightSlot={
            <>
              <Button primary onClick={openCreateModal}>
                <i aria-hidden="true" className="icon far fa-plus" />
                New workspace
              </Button>
              <Button onClick={() => handelLogout()}>
                <i aria-hidden="true" className="far fa-power-off icon !ml-0" />
                Log out
              </Button>
            </>
          }
        />
        <AdminContainer>
          <ContainerShell
            isLoading={loading}
            intro={
              <ContainerShellIntro
                helpLinkLabel="Learn more about workspaces"
                helpLinkUrl="https://help.simplesat.io/en/articles/9756396-workspaces"
                title="Workspaces"
              />
            }
          >
            <Table
              variant="indexPage"
              clickable={false}
              pagination={false}
              dataSource={R.prop('results', data)}
              columns={columns}
              locale={{ emptyText: <EmptyWorkspace /> }}
            />
          </ContainerShell>
        </AdminContainer>
      </Container>
      {isOpenCreateModal && (
        <CreateModal size="tiny">
          <CreateModal.Header>Create workspace</CreateModal.Header>
          <CreateModal.Content>
            <Form onSubmit={handleCreateWorkspace}>
              <Message
                compact
                content="Creating a new workspace will start a separate trial and set up a new account with its
                  own settings, feedback, and users. While this is a new account, you can access it with
                  your current username."
              />
              <Form.Field error={Boolean(workspaceNameError)}>
                <label htmlFor="workspace-name">Workspace name</label>
                <input
                  id="workspace-name"
                  value={workspaceName}
                  onChange={(e) => {
                    setWorkspaceName(e.target.value)
                    if (workspaceNameError) {
                      setWorkspaceNameError('')
                    }
                  }}
                  maxLength={200}
                />
                {workspaceNameError && <ErrorText>{workspaceNameError}</ErrorText>}
              </Form.Field>

              <input type="submit" hidden ref={hiddenRefButton} />
            </Form>
          </CreateModal.Content>
          <CreateModal.Actions>
            <Button onClick={handleCloseWorkspaceModal}>Cancel</Button>
            <Button
              primary
              onClick={() => hiddenRefButton.current.click()}
              disabled={loadingCreateWorkspace}
              loading={loadingCreateWorkspace}
            >
              Create
            </Button>
          </CreateModal.Actions>
        </CreateModal>
      )}

      {isOpenDeleteModal && (
        <DeleteModal size="tiny">
          <DeleteModal.Header>Leave workspace</DeleteModal.Header>
          <DeleteModal.Content>
            <p>If you leave this workspace, you'll need an admin to re-add you to regain access.</p>
          </DeleteModal.Content>
          <DeleteModal.Actions>
            <Button onClick={closeDeleteModal}>Cancel</Button>
            <Button
              negative
              onClick={handleDeleteWorkspace}
              disabled={loadingDeleteWorkspace}
              loading={loadingDeleteWorkspace}
            >
              Leave
            </Button>
          </DeleteModal.Actions>
        </DeleteModal>
      )}
    </React.Fragment>
  )
}

export function EmptyWorkspace() {
  return (
    <Segment padded="very" textAlign="center" basic>
      <h3>It looks like you're not in any workspaces. Let's change that!</h3>
      <p>
        You can create a new workspace, or{' '}
        <a href="" id="contact-support" className="link">
          contact Simplesat support
        </a>{' '}
        if you think there's an issue.
      </p>
    </Segment>
  )
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/pages/SelectWorkspace/index.js